/**
 * @file Simplified data portability management panel with collection selection
 * @version 2.0.0
 * @author Claude Assistant
 * @since 2025-07-09
 */

import { useDataPortability } from '../composables/dataPortability.js';
import { useDb } from '../../core/db.js';
import { useNotifications } from '../../core/errors.js';
import DataExportButton from './DataExportButton.js';
import DataImportZone from './DataImportZone.js';
import CollectionSelector from '../../collections/components/CollectionSelector.js';
import CollectionEditor from '../../collections/components/CollectionEditor.js';

const { ref } = Vue;

export default {
  name: 'DataPortabilityPanel',

  data() {
    return {
      editedCollection: null,
    };
  },

  components: {
    DataExportButton,
    DataImportZone,
    CollectionSelector,
    CollectionEditor,
  },

  template: `
    <div class="data-portability-panel">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">Data Management</h4>
          <small class="text-muted">Export and import your data across different environments</small>
        </div>
        <div class="text-end">
          <small class="text-muted">{{ db.collections.length }} collections, {{ exportableConfigs.length }} configs</small>
        </div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link active"
            id="export-tab"
            data-bs-toggle="tab"
            data-bs-target="#data-pane"
            type="button"
            role="tab"
          >
            <i class="bi-database me-2"></i>
            Data
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            id="export-tab"
            data-bs-toggle="tab"
            data-bs-target="#export-pane"
            type="button"
            role="tab"
          >
            <i class="bi-download me-2"></i>
            Export
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            id="import-tab"
            data-bs-toggle="tab"
            data-bs-target="#import-pane"
            type="button"
            role="tab"
          >
            <i class="bi-upload me-2"></i>
            Import
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            id="danger-tab"
            data-bs-toggle="tab"
            data-bs-target="#danger-pane"
            type="button"
            role="tab"
          >
            <i class="bi-exclamation-triangle me-2"></i>
            Reset
          </button>
        </li>
      </ul>

      <!-- Tab Content -->
      <div class="tab-content border border-top-0 p-3">
        <!-- Data Tab -->
        <div class="tab-pane fade show active" id="data-pane" role="tabpanel">
          <div class="row">
            <div class="col-12">
              <div v-if="db.collections.length === 0" class="text-center py-3">
                <i class="bi-inbox text-muted" style="font-size: 1.5rem;"></i>
                <p class="text-muted mt-2 small">No collections found</p>
              </div>

              <div v-else>
                <div class="d-flex gap-2 mb-2">
                  <button
                    v-for="collection in db.collections"
                    :key="collection.name"
                    class="btn"
                    :class="collection === editedCollection ? 'btn-primary' : 'btn-secondary'"
                    @click="editedCollection = collection"
                  >{{ collection.name }}</button>
                </div>

                <CollectionEditor
                  v-if="editedCollection"
                  :collection="editedCollection"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Export Tab -->
        <div class="tab-pane fade" id="export-pane" role="tabpanel">
          <div class="row">
            <!-- Individual Collection Export -->
            <div class="col-md-6">
              <h6>Individual Collections</h6>
              <p class="text-muted small">Export collections one by one</p>

              <div v-if="db.collections.length === 0" class="text-center py-3">
                <i class="bi-inbox text-muted" style="font-size: 1.5rem;"></i>
                <p class="text-muted mt-2 small">No collections found</p>
              </div>

              <div v-else class="collection-export-list">
                <div v-for="collection in db.collections" :key="collection.name" class="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                  <div>
                    <strong>{{ collection.name }}</strong>
                    <small class="text-muted d-block">{{ collection.items.length }} records</small>
                  </div>
                  <DataExportButton
                    :collection-name="collection.name"
                    variant="outline-primary"
                    size="small"
                    label="Export"
                  />
                </div>
              </div>
            </div>

            <!-- Database Export with Collection Selection -->
            <div class="col-md-6">
              <h6>Database Export</h6>
              <p class="text-muted small">Export multiple collections and configs in a single file</p>

              <div v-if="db.collections.length > 0" class="mb-3">
                <CollectionSelector
                  :collections="db.collections"
                  v-model="selectedExportCollections"
                  :disabled="isExporting"
                />
              </div>

              <!-- Config Selection -->
              <div v-if="exportableConfigs.length > 0" class="mb-3">
                <h6 class="mb-2">Configs</h6>
                <div v-for="config in exportableConfigs" :key="config.name" class="form-check">
                  <input
                    :id="'config-' + config.name"
                    class="form-check-input"
                    type="checkbox"
                    :value="config.name"
                    v-model="selectedExportConfigs"
                    :disabled="isExporting"
                  >
                  <label class="form-check-label" :for="'config-' + config.name">
                    {{ config.name }}
                  </label>
                </div>
              </div>

              <button
                class="btn btn-primary"
                @click="exportSelectedCollections"
                :disabled="isExporting || (selectedExportCollections.length === 0 && selectedExportConfigs.length === 0)"
              >
                <span v-if="isExporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bi-download me-2"></i>
                Export Database ({{ selectedExportCollections.length + selectedExportConfigs.length }})
              </button>

              <div v-if="selectedExportCollections.length === 0 && selectedExportConfigs.length === 0" class="text-muted small mt-2">
                Select collections or configs to export
              </div>
            </div>
          </div>
        </div>

        <!-- Import Tab -->
        <div class="tab-pane fade" id="import-pane" role="tabpanel">
          <p class="text-muted small mb-3">
            Import individual collections or complete database backups.
            Supports both single collection files and full database exports.
          </p>
          <DataImportZone
            :importable-collections="db.collections.map(c => c.name)"
            :max-file-size="50 * 1024 * 1024"
            @import-complete="handleImportComplete"
          />
        </div>

        <!-- Danger Zone Tab -->
        <div class="tab-pane fade" id="danger-pane" role="tabpanel">
          <div class="d-flex justify-content-between align-items-center p-3 border border-danger rounded">
            <div>
              <h6 class="text-danger mb-1">Hard Reset</h6>
              <small class="text-muted">Delete all local data and resync from server</small>
            </div>
            <button class="btn btn-outline-danger" @click="db.hardReset">
              <i class="bi bi-trash me-1"></i>
              Hard Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  `,

  setup() {
    const { exportDatabase, isExporting } = useDataPortability();
    const { success, error, warning } = useNotifications();
    const db = useDb();

    const selectedExportCollections = ref([]);
    const selectedExportConfigs = ref([]);

    // Filter out _user config from exportable configs
    const exportableConfigs = Vue.computed(() =>
      db.configs.filter(c => c.name !== '_user')
    );

    // Auto-select all collections and configs for export by default
    Vue.watch(
      () => [db.collections.length, exportableConfigs.value.length],
      () => {
        if (selectedExportCollections.value.length === 0) {
          selectedExportCollections.value = db.collections.map(c => c.name);
        }
        if (selectedExportConfigs.value.length === 0) {
          selectedExportConfigs.value = exportableConfigs.value.map(
            c => c.name
          );
        }
      },
      { immediate: true }
    );

    const exportSelectedCollections = async () => {
      if (
        selectedExportCollections.value.length === 0 &&
        selectedExportConfigs.value.length === 0
      ) {
        warning('Please select at least one collection or config to export');
        return;
      }

      try {
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `database-backup-${timestamp}.json`;

        const result = await exportDatabase({
          filename,
          collections: selectedExportCollections.value,
          configs: selectedExportConfigs.value,
        });

        const parts = [];
        if (selectedExportCollections.value.length > 0) {
          parts.push(`${selectedExportCollections.value.length} collections`);
        }
        if (selectedExportConfigs.value.length > 0) {
          parts.push(`${selectedExportConfigs.value.length} configs`);
        }

        success(
          `Exported ${parts.join(', ')} with ${result.totalRecords} total records`
        );
      } catch (err) {
        error(`Export failed: ${err.message || 'Unknown error'}`);
      }
    };

    const handleImportComplete = result => {
      if (result.success !== false) {
        success('Import completed successfully');
      }
    };

    return {
      db,
      exportableConfigs,
      selectedExportCollections,
      selectedExportConfigs,
      isExporting,
      exportSelectedCollections,
      handleImportComplete,
    };
  },
};
